import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Table, Button, Input, Space,Tooltip } from "antd";
import { SearchOutlined, DeleteOutlined,CloseCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import config from "../../config";
import { ToastContainer, toast, Bounce } from 'react-toastify';
import SelectUsersMiniTable from "../Commons/SelectUsersMiniTable";
import ExcelQuestionImport from "../Commons/ExcelQuestionImport";
import PermissionGuard from "../Guards/PermissionGuard";
import { PERMISSIONS } from "../../constants/permissions";
import { FaSpinner } from "react-icons/fa";
import QrCodeField from "../Commons/QrCodeField";
import TagMultiFilter from "../Commons/TagMultiFilter";
import { buildQuestionFilter } from "../../utils/gridify";
import {
    getApiErrorMessage,
    isQrCodeConflict,
    normalizeQrCode,
    withOptionalQrCode,
} from "../../utils/qrCode";
import VersionConflictModal from "../Commons/VersionConflictModal";
import {
    extractVersion,
    getVersionConflictMessage,
    isVersionConflict,
} from "../../utils/versionConflict";

const ExamForm = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const initialExam = location.state?.initialExam || null;
    const tagColors = ["bg-green-100 text-green-800", "bg-yellow-100 text-yellow-800", "bg-pink-100 text-pink-800"];
    const [title, setTitle] = useState("");
    const [duration, setDuration] = useState("");
    const [allowStudentChangeTime, setAllowStudentChangeTime] = useState(false);
    const [version, setVersion] = useState(0);
    const [questions, setQuestions] = useState([]);
    const [tags, setTags] = useState([]);
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [examPdf, setExamPdf] = useState("");
    const [pdfOptions, setPdfOptions] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [qrCode, setQrCode] = useState("");
    const [qrCodeError, setQrCodeError] = useState("");
    const [isExcelImportOpen, setIsExcelImportOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isOutdated, setIsOutdated] = useState(false);
    const [versionConflict, setVersionConflict] = useState({
        open: false,
        message: "",
    });
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [questionNumber, setQuestionNumber] = useState(1);
const [searchFilters, setSearchFilters] = useState({
        title: "",
        QCode: "",
        tag: []
    });
        const [loading, setLoading] = useState(false);
        const [tagInput, setTagInput] = useState("");
    
    const [pagination, setPagination] = useState({
            current: 1,
            pageSize: 10,
            total: 0,
        });
        const ClearSuffix = ({ onClick, style }) => (
        <Tooltip title="پاک‌کردن">
            <CloseCircleOutlined
                onClick={onClick}
                style={{ cursor: "pointer", ...style, textAlign: 'left' }}
                role="button"
                aria-label="clear"
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
            />
        </Tooltip>
    );
    const handleSearchChange = (e, field) => {
        const value = e?.target ? e.target.value : e;
        setSearchFilters((prev) => ({ ...prev, [field]: value }));
    };
    const fetchQuestionsWithFilters = (override = {}) => {
        const effectiveFilters = { ...searchFilters, ...override };
        fetchQuestions(1, pagination.pageSize, effectiveFilters);
    };
    const fetchQuestions = async (page = 1, pageSize = 10, filtersFromCaller = searchFilters) => {
        setLoading(true);
        try {
            const requestBody = {
                Page: page,
                PageSize: pageSize,
                Filter: buildQuestionFilter(filtersFromCaller),
                OrderBy: "",
            };

            const response = await axios.post(
                `${config.apiBaseUrl}/api/Questions/GetList`,
                requestBody,
                {
                    headers: {
                        "Content-Type": "application/json",
                        HeaderKey: config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );

            const data = response.data.data || {};
            const items = data.questions || [];
            const total = data.totalCount ?? data.count ?? 0;


            setQuestions(items);
            setPagination((prev) => ({ ...prev, total, current: page, pageSize }));
        } catch (err) {
            console.error("Fetch questions error:", err);
            toast.error(<p dir="rtl" className="font-custom">جستجو با خطا مواجه شد</p>);
        } finally {
            setLoading(false);
        }
    };

    const normalizeExamQuestion = (examQuestion, fallbackOrder = 0) => {
        const question = examQuestion?.question ?? examQuestion;
        const id =
            examQuestion?.questionId ??
            question?.id ??
            question?.questionId;

        if (id == null) return null;

        return {
            ...question,
            id,
            key: id,
            title: question?.title ?? examQuestion?.title ?? id,
            qCode: question?.qCode ?? examQuestion?.qCode,
            order: examQuestion?.order ?? question?.order ?? fallbackOrder,
        };
    };

    const fetchExamById = async (examId = initialExam?.id) => {
        if (!examId) return null;

        const response = await axios.post(
            `${config.apiBaseUrl}/api/Exam/GetList`,
            {
                Page: 1,
                PageSize: 1,
                Filter: `id=${examId}`,
                OrderBy: "",
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    HeaderKey: config.HeaderKey,
                },
                withCredentials: true,
            }
        );

        const payload = response.data?.data;
        const exam = Array.isArray(payload)
            ? payload[0]
            : Array.isArray(payload?.data)
                ? payload.data[0]
                : payload;

        return exam || null;
    };

    const applyExamToForm = async (exam) => {
        if (!exam) return;

        setTags(exam.tags || []);
        setTitle(exam.title || "");
        setDuration(exam.duration != null ? String(exam.duration) : "");
        setAllowStudentChangeTime(Boolean(exam.allowStudentChangeTime ?? exam.AllowStudentChangeTime));
        setExamPdf(exam.pdfId || "");
        setQrCode(exam.qrCode ?? exam.QrCode ?? "");
        setVersion(extractVersion(exam) ?? 0);
        setQrCodeError("");

        const selQs = (exam.examQuestions || [])
            .map((eq, index) => normalizeExamQuestion(eq, index + 1))
            .filter(Boolean);
        setSelectedQuestions(selQs);
        const maxOrder = selQs.reduce((max, q) => Math.max(max, q.order || 0), 0);
        setQuestionNumber(maxOrder + 1);

        if (Array.isArray(exam.users) && exam.users.length) {
            setSelectedUsers(exam.users);
        } else if (Array.isArray(exam.userIds) && exam.userIds.length) {
            try {
                const res = await axios.get(
                    `${config.apiBaseUrl}/api/users/getbyids`,
                    {
                        params: { ids: exam.userIds.join(",") },
                        withCredentials: true,
                        headers: { HeaderKey: config.HeaderKey },
                    }
                );
                setSelectedUsers(res.data || []);
            } catch {
                setSelectedUsers([]);
            }
        } else {
            setSelectedUsers([]);
        }

        setIsOutdated(false);
    };

    const fetchExamQuestions = async () => {
        const exam = await fetchExamById();
        return (exam?.examQuestions ?? []).map(normalizeExamQuestion).filter(Boolean);
    };

    const handleExamImportSuccess = async (result) => {
        const previousSelections = [...selectedQuestions];
        let importedSelections = [];

        try {
            importedSelections = await fetchExamQuestions();
        } catch (error) {
            console.error("Refresh imported exam questions error:", error);
        }

        if (importedSelections.length === 0) {
            const importedById = new Map(
                (result.importedQuestions ?? [])
                    .map((question, index) => normalizeExamQuestion(question, index + 1))
                    .filter(Boolean)
                    .map((question) => [question.id, question])
            );

            importedSelections = (result.importedQuestionIds ?? []).map((id, index) => {
                const fromResult = importedById.get(id);
                const fromCurrentPage = questions.find((question) => question.id === id);
                return (
                    fromResult ??
                    (fromCurrentPage
                        ? { ...fromCurrentPage, order: index + 1 }
                        : { id, key: id, title: id, order: index + 1 })
                );
            });
        }

        const merged = new Map(previousSelections.map((question) => [question.id, question]));
        let nextOrder = previousSelections.reduce(
            (max, question) => Math.max(max, question.order || 0),
            0
        ) + 1;

        importedSelections.forEach((question) => {
            if (!merged.has(question.id)) {
                merged.set(question.id, {
                    ...question,
                    order: question.order || nextOrder++,
                });
            }
        });

        updateSelectedQuestions(Array.from(merged.values()));
        await fetchQuestions(pagination.current, pagination.pageSize, searchFilters);
        toast.success(
            <p dir="rtl" className="font-custom">
                سوالات اکسل پردازش و فهرست سوالات به‌روزرسانی شد
            </p>
        );
    };

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [qRes, pdfRes] = await Promise.all([
                    axios.get(
                        `${config.apiBaseUrl}/api/Questions/GetMinimalList`,
                        { withCredentials: true, headers: { HeaderKey: config.HeaderKey } }
                    ),
                    /*axios.get(
                        `${config.apiBaseUrl}/api/video/GetFilteredList?filter=FileType=1`,
                        { withCredentials: true, headers: { HeaderKey: config.HeaderKey } }
                    ),*/
                ]);
                
                //setPdfOptions(pdfRes?.data.videos || []);
                
                // Fetch first page of questions with pagination
                await fetchQuestions(1, pagination.pageSize);
                
                if (initialExam?.id) {
                    try {
                        const exam = await fetchExamById(initialExam.id);
                        await applyExamToForm(exam || initialExam);
                    } catch {
                        await applyExamToForm(initialExam);
                    }
                }
            } catch (err) {
                toast.error("خطا در بارگذاری داده‌ها");
            }
        };
        fetchAll();
    }, []);

    const updateSelectedQuestions = (next) => {
        setSelectedQuestions(next);
        if (next.length === 0) {
            setQuestionNumber(1);
        } else {
            const maxOrder = next.reduce((max, q) => Math.max(max, q.order || 0), 0);
            setQuestionNumber(maxOrder + 1);
        }
    };

    const availableRowSelection = {
        selectedRowKeys: selectedQuestions.map(q => q.id),
        onChange: (selectedRowKeys) => {
            if (selectedRowKeys.length === 0) {
                updateSelectedQuestions([]);
                return;
            }

            const selectedMap = new Map(selectedQuestions.map(q => [q.id, q]));
            const next = [];
            let nextOrder =
                selectedQuestions.length === 0
                    ? 1
                    : Math.max(
                          questionNumber - 1,
                          ...selectedQuestions.map((q) => q.order || 0)
                      ) + 1;

            selectedRowKeys.forEach((id) => {
                if (selectedMap.has(id)) {
                    next.push(selectedMap.get(id));
                } else {
                    const q = questions.find((x) => x.id === id);
                    if (q) {
                        next.push({ ...q, order: nextOrder });
                        nextOrder += 1;
                    }
                }
            });

            updateSelectedQuestions(next);
        },
        preserveSelectedRowKeys: true
    };

    const selectedQuestionColumns = [
        {
            title: "حذف",
            className: "font-custom",
            key: "remove",
            render: (_, record) => (
                <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() =>
                        updateSelectedQuestions(
                            selectedQuestions.filter((q) => q.id !== record.id)
                        )
                    }
                    size="small"
                />
            ),
        },
        {
            title: "شماره سوال",
            align: "center",
            className: "font-custom text-center",
            dataIndex: "order",
            key: "order",
            render: (_, record) => (
                <input
                    type="number"
                    className="border rounded text-center px-2 py-1 w-20"
                    value={record.order ?? 0}
                    onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setSelectedQuestions(prev =>
                            prev.map(q => (q.id === record.id ? { ...q, order: Number.isNaN(v) ? 0 : v } : q))
                        );
                    }}
                />
            ),
        },
        { title: "عنوان", align: "right", className: "font-custom text-right", dataIndex: "title", key: "title" },
    ];

    const handleTagChange = (e) => {
        const value = e.target.value;
        const separators = [",", "،"];

        if (separators.some(sep => value.includes(sep))) {
            const newTag = value.replace(/[,،]/g, "").trim();
            if (newTag && !tags.includes(newTag)) {
                setTags(prev => [...prev, newTag]);
            }
            setTagInput("");
        } else {
            setTagInput(value);
        }
    };

    const removeTag = (index) => {
    setTags((prev) => prev.filter((_, i) => i !== index));
};
    const handleVersionConflictRefresh = async () => {
        setIsRefreshing(true);
        try {
            const exam = await fetchExamById(initialExam?.id);
            if (!exam) throw new Error("exam not found");
            await applyExamToForm(exam);
            setVersionConflict({ open: false, message: "" });
            toast.success(
                <p dir="rtl" className="font-custom">اطلاعات آزمون بازخوانی شد</p>
            );
        } catch {
            toast.error(<p dir="rtl" className="font-custom">خطا در بازخوانی آزمون</p>);
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleVersionConflictCancel = () => {
        setVersionConflict({ open: false, message: "" });
        navigate(-1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isOutdated) {
            setVersionConflict((prev) => ({ ...prev, open: true }));
            return;
        }
        setIsSubmitting(true);
        setIsLoading(true);

        setQrCodeError("");

        const commonPayload = withOptionalQrCode({
            title,
            duration: duration ? parseInt(duration, 10) : null,
            questions: selectedQuestions.map(q => ({ questionId: q.id, order: q.order || 0 })),
            examPdf: examPdf || null,
            tags: tags || null,
            allowStudentChangeTime,
        }, qrCode);

        try {
            if (initialExam?.id) {
                const payload = {
                    ...commonPayload,
                    id: initialExam.id,
                    version: version ?? 0,
                    NewUsers: selectedUsers.map(u => u.userID),
                    NewBanks: [], 
                };

                const response = await axios.post(`${config.apiBaseUrl}/api/exam/Update`, payload, {
                    headers: { "Content-Type": "application/json", HeaderKey: config.HeaderKey },
                    withCredentials: true,
                });
                setQrCode(response.data?.qrCode ?? normalizeQrCode(qrCode));
                const nextVersion = extractVersion(response.data);
                if (nextVersion != null) setVersion(nextVersion);
                toast.success("آزمون با موفقیت به‌روزرسانی شد");

            } else {
                
                const payload = {
                    ...commonPayload,
                    userIds: selectedUsers.map(u => u.userID),
                };
                const response = await axios.post(`${config.apiBaseUrl}/api/exam/Create`, payload, {
                    headers: { "Content-Type": "application/json", HeaderKey: config.HeaderKey },
                    withCredentials: true,
                });
                setQrCode(response.data?.qrCode ?? normalizeQrCode(qrCode));
                toast.success(<p dir="rtl" className="font-custom">آزمون با موفقیت ایجاد شد</p>);
                
            }
            navigate(-1);
        } catch (err) {
            if (isVersionConflict(err)) {
                setIsOutdated(true);
                setVersionConflict({
                    open: true,
                    message: getVersionConflictMessage(err),
                });
            } else {
                const msg = getApiErrorMessage(err, "خطا در ثبت اطلاعات آزمون");
                if (isQrCodeConflict(err)) {
                    setQrCodeError(msg);
                }
                toast.error(<p dir="rtl" className="font-custom">{msg}</p>);
            }
        } finally {
            setIsSubmitting(false);
        setIsLoading(false);
        }
    };


    return (
        <form className="max-w-6xl mx-auto p-6 bg-white rounded shadow" onSubmit={handleSubmit}>
            <h2 className="text-xl text-center font-custom font-bold mb-6">{initialExam ? "ویرایش آزمون" : "ایجاد آزمون"}</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                
                <QrCodeField
                    value={qrCode}
                    error={qrCodeError}
                    onChange={(value) => {
                        setQrCode(value);
                        setQrCodeError("");
                    }}
                />
                <div>
                    <div className="mb-4">
                        <label htmlFor="videoId" className="block mb-1 text-right font-bold">شناسه یکتا پی‌دی‌اف (اختیاری)</label>
                        <input type="text" id="videoId" name="videoId" value={examPdf || ''} onChange={e => setExamPdf(e.target.value)} className={`border rounded px-3 py-2 w-full dir-ltr`} />
                    </div>
                </div>
                <div>
                    <label className="block text-right font-custom mb-1 font-medium">مدت آزمون (ثانیه)</label>
                    <input type="number" className="border rounded px-3 py-2 w-full" value={duration} onChange={e => setDuration(e.target.value)} />
                </div>
                <div>
                    <label className="block text-right font-custom mb-1 font-medium ">عنوان</label>
                    <input className="border rounded px-3 py-2 w-full font-custom" dir="rtl" value={title} onChange={e => setTitle(e.target.value)} required />
                </div>
            </div>
            <div>
                <div className="p-3 border rounded-md w-full text-right">
                    <div className="flex flex-wrap gap-2 mb-2">
                        {tags.map((tag, index) => (
                            <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full flex items-center">
                                {tag}
                                <button
                                    type="button"
                                    onClick={() => removeTag(index)}
                                    className="ml-2 text-red-500 font-bold hover:text-red-700"
                                >
                                    ✖
                                </button>
                            </span>
                        ))}
                    </div>

                    <input
                        type="text"
                        name="tags"
                        placeholder="تگ‌ها را با کاما یا ویرگول جدا کنید"
                        value={tagInput}
                        onChange={handleTagChange}
                        className="p-2 border rounded-md w-full text-right focus:outline-none"
                        dir="rtl"
                    />
                </div>
            </div>
            <div className="mb-8">
                <div className="flex items-center my-4">
                    <div className="flex-grow border-t border-gray-300" />
                    <h3 className="mx-4 text-lg font-bold text-gray-600 whitespace-nowrap font-custom">انتخاب سوالات</h3>
                    <div className="flex-grow border-t border-gray-300" />
                </div>
                {initialExam?.id && (
                    <div className="flex justify-start mb-4">
                        <PermissionGuard
                            permissions={[
                                PERMISSIONS.QUESTIONS_CREATE,
                                PERMISSIONS.QUESTIONS_EDIT,
                                PERMISSIONS.EXAMS_EDIT,
                            ]}
                            requireAll
                        >
                            <Button
                                type="primary"
                                htmlType="button"
                                className="font-custom"
                                onClick={() => setIsExcelImportOpen(true)}
                            >
                                ورود سوالات از اکسل
                            </Button>
                        </PermissionGuard>
                    </div>
                )}
                <Table
                    rowSelection={availableRowSelection}
                    columns={[
                        {
                            title: "کد سوال",
                            dataIndex: "qCode",
                            key: "qCode",
                            width: 150,
                            className: "font-custom ",
                            align: "center",
                            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                                <div style={{ padding: 8 }}>
                                    <Input
                                        type='number'
                                        placeholder="جستجوی کد سوال"
                                        value={searchFilters.QCode}
                                        onChange={(e) => handleSearchChange(e, "QCode")}
                                        onPressEnter={() => fetchQuestionsWithFilters()}
                                        suffix={
                                            <ClearSuffix
                                                onClick={() => {
                                                    fetchQuestionsWithFilters({ QCode: "" });
                                                    setSearchFilters((prev) => ({ ...prev, QCode: "" }));
                                                }}
                                                style={{ visibility: searchFilters.QCode ? "visible" : "hidden" }}
                                            />
                                        }
                                        style={{ marginBottom: 8, display: "flex", direction: "rtl" }}
                                    />
                                    <Space>
                                        <Button
                                            type="primary"
                                            onClick={() => fetchQuestionsWithFilters()}
                                            icon={<SearchOutlined size={16} />}
                                            size="small"
                                            className="font-custom"
                                        >
                                            جستجو
                                        </Button>
                                    </Space>
                                </div>
                            )
                        }, {
                            title: "تگ ها",
                            dataIndex: "tags",
                            key: "tags",
                            width: 180,
                            align: "center",
                            className: "font-custom text-center",
                            render: (tags) => (
                                <div className="flex flex-wrap gap-1 justify-center">
                                    {tags?.map((tag, index) => (
                                        <span
                                            key={index}
                                            className={`${tagColors[index % tagColors.length]} text-xs font-semibold px-2 py-1 rounded`}>
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            ),
                            filterDropdown: () => (
                                <TagMultiFilter
                                    value={searchFilters.tag}
                                    placeholder="تگ‌های سوال را وارد کنید"
                                    onChange={(value) =>
                                        setSearchFilters((prev) => ({ ...prev, tag: value }))
                                    }
                                    onSearch={(value) => {
                                        const tag = Array.isArray(value)
                                            ? value
                                            : searchFilters.tag;
                                        fetchQuestionsWithFilters({ tag });
                                    }}
                                />
                            )
                        },
                        {
                            title: "عنوان",
                            dataIndex: "title",
                            key: "title",
                            className: "font-custom ",
                            width: 500,
                            align: "right",
                            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                                <div style={{ padding: 8 }}>
                                    <Input
                                        placeholder="جستجوی عنوان"
                                        value={searchFilters.title}
                                        onChange={(e) => handleSearchChange(e, "title")}
                                        onPressEnter={() => fetchQuestionsWithFilters()}
                                        suffix={
                                            <ClearSuffix
                                                onClick={() => {
                                                    fetchQuestionsWithFilters({ title: "" });
                                                    setSearchFilters((prev) => ({ ...prev, title: "" }));
                                                }}
                                                style={{ visibility: searchFilters.title ? "visible" : "hidden" }}
                                            />
                                        }
                                        style={{ marginBottom: 8, display: "flex", direction: "rtl" }}
                                    />
                                    <Space>
                                        <Button
                                            type="primary"
                                            onClick={() => fetchQuestionsWithFilters()}
                                            icon={<SearchOutlined size={16} />}
                                            size="small"
                                            className="font-custom"
                                        >
                                            جستجو
                                        </Button>
                                    </Space>
                                </div>
                            )
                        }]}
                    dataSource={questions}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        onChange: (page, pageSize) => {
                            setPagination((prev) => ({ ...prev, current: page, pageSize }));
                            fetchQuestions(page, pageSize);
                        },
                        showTotal: (total, range) => `${range[0]}-${range[1]} از ${total}`,
                    }}
                />
            </div>

            <div className="mb-8">
                <h3 className="text-lg font-semibold mb-2 text-right font-custom">سوالات انتخاب‌شده</h3>
                <Table
                    columns={selectedQuestionColumns}
                    dataSource={selectedQuestions}
                    rowKey="id"
                    pagination={{
                        pageSize: 5,
                        showTotal: (total, range) => `${range[0]}-${range[1]} از ${total}`,
                    }}
                />
                <div className="mt-3 flex gap-2">
                    <Button className="font-custom" onClick={() => setSelectedQuestions(prev => [...prev].sort((a, b) => (a.order || 0) - (b.order || 0)))}>مرتب‌سازی بر اساس شماره سوال</Button>
                    <Button className="font-custom" danger onClick={() => updateSelectedQuestions([])}>حذف همه</Button>
                </div>
            </div>

            <div className="mb-8">
                <SelectUsersMiniTable value={selectedUsers} onChange={setSelectedUsers} apiUrl="/api/users/getuserslist" pageSize={12} />
            </div>
            <label className="mb-4 flex items-center justify-end gap-3 font-custom cursor-pointer">
                <span>اجازه تغییر زمان آزمون توسط دانشجو</span>
                <input
                    type="checkbox"
                    checked={allowStudentChangeTime}
                    onChange={(event) => setAllowStudentChangeTime(event.target.checked)}
                    className="h-4 w-4"
                />
            </label>
            <button
                type="submit"
                disabled={isLoading || isOutdated}
                className="w-full flex justify-center items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg text-sm px-4 py-2.5 transition-colors duration-300 disabled:opacity-70"
            >
                {isLoading ? <FaSpinner className="animate-spin" /> : initialExam ? "به‌روزرسانی آزمون" : "ایجاد آزمون"}
            </button>
            {initialExam?.id && (
                <ExcelQuestionImport
                    open={isExcelImportOpen}
                    onClose={() => setIsExcelImportOpen(false)}
                    examId={initialExam.id}
                    onSuccess={handleExamImportSuccess}
                    title="ورود سوالات اکسل به آزمون"
                />
            )}
            <VersionConflictModal
                open={versionConflict.open}
                message={versionConflict.message}
                refreshing={isRefreshing}
                onRefresh={handleVersionConflictRefresh}
                onCancel={handleVersionConflictCancel}
            />
             <ToastContainer
                position="bottom-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                className={"font-custom text-center rtl"}
                pauseOnHover
                theme="dark"
                transition={Bounce}
            />
        </form>
    );
};

export default ExamForm;